import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { EExerciseMode, EExerciseView, ELedView } from './constants';
import { EExtraMode, IInitDifficultyPayload, IInternalFeedbackResult, IOpenExercisePayload } from './types';

export interface IExerciseState {
  loading: boolean;
  // current workout id
  workoutId: number;
  // current view
  view: EExerciseView;
  // recommended difficulty
  startDifficulty: number;
  // max reached difficulty
  reachedDifficulty: number;
  // difficulty
  difficulty: number;
  // current goal
  goal: number;
  // last score to calculate delta
  lastScore: number;
  // total amount of runs for current exercise
  exerciseRun: number;
  // show unity preloader static svg
  staticPreloader: boolean;
  // show unity preloader lottie flag
  unityPreloader: boolean;
  // led feature view
  led: ELedView;
  // exercise mode
  exerciseMode: EExerciseMode | undefined;
}

export const initialState: IExerciseState = {
  loading: true,
  workoutId: 0,
  view: EExerciseView.None,
  startDifficulty: 0,
  reachedDifficulty: 0,
  difficulty: 0,
  goal: 0,
  lastScore: 0,
  exerciseRun: 0,
  staticPreloader: false,
  unityPreloader: false,
  led: ELedView.None,
  exerciseMode: undefined,
};

export const statePropName = 'exercise';

const exerciseSlice = createSlice({
  name: statePropName,
  initialState,
  reducers: {
    /**
     * action fired when exercise page is rendered
     * @param exerciseLink optional exercise id in lowercase received from router
     * @param workoutIndex optional index of the workout in current session which to replace received from history state
     */
    openExercise: {
      reducer: (state, action: PayloadAction<IOpenExercisePayload>) => {
        return { ...initialState };
      },
      prepare: (exerciseLink?: string, workoutIndex?: number) => {
        return { payload: { exerciseLink, workoutIndex } };
      },
    },
    /**
     * action fired when all necessary data for the exercise page is received/calculated
     * @param workoutId actual workout id which will be started
     */
    exercisePageReady: (state, action: PayloadAction<number>) => {
      state.loading = false;
      state.workoutId = action.payload;
      state.view = EExerciseView.Intro;
    },
    /**
     * action fired when recommended workout difficulty for current workout calculated
     * @param startDifficulty actual recommended difficulty
     * @param reachedDifficulty max difficulty reached by user
     */
    initDifficulty: {
      reducer: (state, action: PayloadAction<IInitDifficultyPayload>) => {
        state.startDifficulty = action.payload.startDifficulty;
        state.reachedDifficulty = action.payload.reachedDifficulty;
        state.difficulty = 0;
      },
      prepare: (startDifficulty: number, reachedDifficulty: number) => {
        return { payload: { startDifficulty, reachedDifficulty } };
      },
    },
    setGoal: (state, action: PayloadAction<number>) => {
      state.goal = action.payload;
    },
    setLastScore: (state, action: PayloadAction<number>) => {
      state.lastScore = action.payload;
    },
    setExerciseRun: (state, action: PayloadAction<number>) => {
      state.exerciseRun = action.payload;
    },
    /**
     * action fired when user want to start exercise
     * @param {number} difficulty user defined (if changed) difficulty level
     */
    startExercise: (state, action: PayloadAction<number>) => {
      state.view = EExerciseView.Workout;
      state.exerciseMode = EExerciseMode.Training;
      state.difficulty = action.payload;
    },
    /**
     * action fired when user want to start exercise in learning mode
     * @param {number} difficulty user defined difficulty level
     */
    startExerciseLearning: (state, action: PayloadAction<number>) => {
      state.view = EExerciseView.Workout;
      state.exerciseMode = EExerciseMode.Learning;
      state.difficulty = action.payload;
    },
    /**
     * action fired when exercise was completed and we need to switch to exercise evaluation screen
     */
    completeExercise: (state) => {
      state.view = EExerciseView.Evaluation;
      state.exerciseMode = undefined;
    },
    skipExercise: (state, action: PayloadAction<number>) => {},
    restartExercise: (state) => {
      state.view = EExerciseView.Intro;
    },
    repeatExercise: (state) => {},
    completeFirstSession: (state) => {
      state.view = EExerciseView.FirstSessionFinished;
    },
    completeSession: (state) => {
      state.view = EExerciseView.Session;
    },
    showUnityPreloader: (state) => {
      state.view = EExerciseView.Workout;
      state.staticPreloader = true;
      state.unityPreloader = false;
    },
    showUnityPreloaderLottie: (state) => {
      state.staticPreloader = false;
      state.unityPreloader = true;
    },
    hideUnityPreloader: (state) => {
      state.staticPreloader = false;
      state.unityPreloader = false;
    },
    loadUnityPreloader: (state) => {},
    unlockPremium: (state) => {},
    postWorkoutFeedback: (state, action: PayloadAction<IInternalFeedbackResult>) => {},
    confirmDifficultyChange: (state) => {
      state.led = ELedView.Confirmation;
    },
    difficultyChangeConfirmationAnswer: (state, action: PayloadAction<boolean>) => {
      state.led = ELedView.Hide;
      state.loading = true;
    },
    hardDifficulty: (state) => {
      state.led = ELedView.Decrease;
    },
    difficultyDecreaseAnswer: (state, action: PayloadAction<boolean>) => {
      state.led = ELedView.Hide;
      state.loading = true;
    },
    easyDifficulty: (state) => {
      state.led = ELedView.Increase;
    },
    difficultyIncreaseAnswer: (state, action: PayloadAction<boolean>) => {
      state.led = ELedView.Hide;
      state.loading = true;
    },
    cancelDifficulty: (state) => {
      state.led = ELedView.Hide;
    },
    difficultySaved: (state) => {
      state.loading = false;
    },
    changeView: (state, action: PayloadAction<EExerciseView>) => {
      state.view = action.payload;
      state.unityPreloader = false;
      state.staticPreloader = false;
    },
    /**
     * action fired when user want to start exercise in a different mode
     * we preserve difficulty selected by user to use in learning mode on a later view
     * @param {number} difficulty difficulty level preserved to start learning mode with this difficulty
     */
    openExtraModes: (state, action: PayloadAction<number>) => {
      state.view = EExerciseView.Modes;
    },
    /**
     * action fired when user selects an extra mode
     * @param {EExtraMode} mode the mode selected by user
     */
    selectExtraMode: (state, action: PayloadAction<EExtraMode>) => {},
  },
});

export const ExerciseActions = exerciseSlice.actions;
export default exerciseSlice.reducer;
